/**
 * Passkeys in Teslyn Cloud (decision 804, docs/specs/cloud/sign-in-security.md §5): Supabase Auth's passkeys
 * (`signInWithPasskey`, `registerPasskey`, `passkey.list` and `passkey.delete`, supabase-js 2.118). A passkey is
 * added from "My sign-in" by someone already signed in, so it never makes an account: invitations stay the only way
 * in. Signing in with one is the first step only; two-step sign-in still asks for the authenticator app's code.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export interface Passkey {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
}

/** The words for a passkey that did not work. */
export function passkeyError(e: unknown): string {
  const err = (e ?? {}) as { code?: unknown; name?: unknown; message?: unknown };
  const code = typeof err.code === 'string' ? err.code : '';
  const name = typeof err.name === 'string' ? err.name : '';
  const msg = typeof err.message === 'string' ? err.message : String(e);
  if (
    code === 'ERROR_CEREMONY_ABORTED' ||
    name === 'NotAllowedError' ||
    /not allowed|abort|cancel/i.test(msg)
  )
    return 'The passkey was not used: the request was cancelled or timed out. Try again when you are ready.';
  if (code === 'passkey_disabled' || /passkey.*disabled|disabled.*passkey/i.test(msg))
    return 'Passkeys are not switched on for this portal yet. Use the email link, Microsoft or Google.';
  if (code === 'webauthn_challenge_expired' || /challenge.*expired/i.test(msg))
    return 'The passkey request expired. Try again.';
  if (code === 'too_many_passkeys') return 'You have as many passkeys as Teslyn allows. Remove one first.';
  if (code === 'user_banned') return 'This sign-in is switched off. Ask your administrator.';
  if (code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED')
    return 'This device already holds a passkey for your sign-in: you can use it to sign in.';
  if (
    code === 'ERROR_INVALID_DOMAIN' ||
    code === 'ERROR_INVALID_RP_ID' ||
    /relying party|rp ?id|origin/i.test(msg)
  )
    return 'Passkeys do not work on this address. Open your organisation’s Teslyn Cloud address and try again.';
  if (
    code === 'webauthn_verification_failed' ||
    /verification failed|not found|unknown credential/i.test(msg)
  )
    return 'This passkey is not known to Teslyn. Sign in another way, then add the passkey again in My sign-in.';
  return `The passkey did not work: ${msg}`;
}

/** Sign in with a passkey on this device or a phone nearby. Throws the words to show. */
export async function signInWithPasskey(sb: SupabaseClient): Promise<void> {
  const { error } = await sb.auth.signInWithPasskey();
  if (error) throw new Error(passkeyError(error));
}

/** The signed-in person's passkeys, newest first. */
export async function listPasskeys(sb: SupabaseClient): Promise<Passkey[]> {
  const { data, error } = await sb.auth.passkey.list();
  if (error) throw new Error(`Could not read your passkeys: ${error.message}`);
  return (data ?? [])
    .map((p) => ({
      id: p.id,
      name: p.friendly_name?.trim() || 'Passkey',
      createdAt: p.created_at,
      lastUsedAt: p.last_used_at ?? null,
    }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Add a passkey for the signed-in person (the browser asks for the fingerprint, face, PIN or phone). */
export async function addPasskey(sb: SupabaseClient): Promise<void> {
  const { error } = await sb.auth.registerPasskey();
  if (error) throw new Error(passkeyError(error));
}

export async function removePasskey(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.auth.passkey.delete({ passkeyId: id });
  if (error) throw new Error(`Could not remove the passkey: ${error.message}`);
}
